<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useWebActivity, gameplayApi, activityStorage } from '../../services/activities/webAdapter.js'
import { createNewbiePackageRecovery } from '../../services/activities/newbiePackageRecovery.mjs'
import { createFirstDepositApplication } from '../../services/activities/firstDepositApplication.mjs'
import GameplayRecovery from './GameplayRecovery.vue'
import PredictionReceiptRecovery from './PredictionReceiptRecovery.vue'
import MemberApplication from './MemberApplication.vue'
import VoteReceiptRecovery from './VoteReceiptRecovery.vue'
import VoteRecordList from './VoteRecordList.vue'
import FirstDepositReceipt from './FirstDepositReceipt.vue'
import SignInHistoryCalendar from './SignInHistoryCalendar.vue'

const props = defineProps({ activityId: { type: [String, Number], required: true } })
const { getAuth, getToken, isLoggedIn, navigate, openAuth } = useWebActivity()
const active = computed(() => isLoggedIn?.value !== false && Boolean(getToken()))
const actor = () => getAuth()?.memberId || ''
const packageView = ref({ visible: false }), depositView = ref({ visible: false })
const showVotes = ref(false), showSignHistory = ref(false), fullSignRewards = ref(false)
const packageRecovery = createNewbiePackageRecovery({ recover: gameplayApi.newbiePackage, recoverRequest: gameplayApi.recover, identity: actor, token: getToken, storage: activityStorage, changed: value => { packageView.value = value } })
// Recovery uses only GET/result APIs; no submit capability is supplied.
const depositRecovery = createFirstDepositApplication({ api: { firstDepositOriginal: gameplayApi.firstDepositOriginal, firstDepositApplicationResult: gameplayApi.firstDepositApplicationResult }, identity: actor, token: getToken, storage: activityStorage, changed: value => { depositView.value = value } })
const time = value => value ? new Date(Number(value) * 1000).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false }) : '—'
function openOrder(id) { navigate(`/mine/prizes/${encodeURIComponent(id)}`) }
function refresh() {
  packageRecovery.pause(); depositRecovery.pause(); showVotes.value = false; showSignHistory.value = false; fullSignRewards.value = false
  if (active.value) { void packageRecovery.open(props.activityId); void depositRecovery.open(props.activityId) }
}
watch(() => [props.activityId, getToken(), actor(), active.value], refresh, { immediate: true })
function synchronize(event) {
  if (!event.key || event.key === 'kaiyun_web_token' || event.key.startsWith('web:kg_newbie_package:v1:') || event.key.startsWith('web:kg_first_deposit:v1:')) refresh()
}
window.addEventListener('storage', synchronize)
onBeforeUnmount(() => { packageRecovery.pause(); depositRecovery.pause(); window.removeEventListener('storage', synchronize) })
</script>

<template>
  <section class="activity-history-recovery" aria-label="原活动参与记录">
    <h2>原活动参与记录</h2>
    <p>活动当前不可用，仍可核对本人的原申请、领取结果和已公布赛果。</p>
    <template v-if="active">
      <MemberApplication :activity-id="activityId" history-only @login="openAuth('login')" />
      <GameplayRecovery :activity-id="activityId" @order="openOrder" />
      <section v-if="packageView.visible" class="history-receipt" aria-label="原新人礼包结果" :aria-busy="packageView.loading">
        <h3>原新人礼包结果</h3>
        <p v-if="packageView.requestId">请求编号：{{ packageView.requestId }}</p>
        <p v-if="packageView.loading" role="status">正在核对原领取结果…</p>
        <p v-if="packageView.error" role="alert">{{ packageView.error }}</p>
        <template v-if="packageView.result"><p>{{ packageView.result.claimed ? '礼包已领取' : packageView.result.reason }}</p><p>{{ packageView.result.claimed ? '已到账奖励' : '申请奖励' }}：{{ packageView.result.rewardAmount }} {{ packageView.result.currency }}</p><p v-if="packageView.result.claimed">领取时间：{{ time(packageView.result.completedAt) }}（北京时间）</p></template>
        <section v-for="gift in packageView.gifts" :key="gift.key" class="history-original-gift"><h4>{{ gift.title }}，{{ gift.claimed ? '已领取' : gift.reason }}</h4><p>{{ gift.claimed ? '已到账奖励' : '申请奖励' }}：{{ gift.rewardAmount }} CNY</p><p>请求编号：{{ gift.requestId }}</p><p v-if="gift.claimed">领取时间：{{ time(gift.completedAt) }}（北京时间）</p></section>
        <button type="button" :disabled="packageView.loading" @click="packageRecovery.open(activityId)">重新核对原礼包</button>
      </section>
      <FirstDepositReceipt :view="depositView" @refresh="depositRecovery.refresh" />
      <VoteReceiptRecovery :activity-id="activityId" @records="showVotes = true" />
      <VoteRecordList v-if="showVotes" :activity-id="activityId" />
      <PredictionReceiptRecovery :activity-id="activityId" />
      <section class="history-receipt" aria-label="原签到历史">
        <h3>签到历史</h3>
        <button v-if="!showSignHistory" type="button" @click="showSignHistory = true">查询签到历史</button>
        <template v-else>
          <p>当前活动不可参与，以下为本人签到历史。</p>
          <SignInHistoryCalendar :activity-id="activityId" history-only :full-rewards="fullSignRewards" @view-all="fullSignRewards = true" />
          <button v-if="fullSignRewards" type="button" @click="fullSignRewards = false">返回签到月历</button>
          <button type="button" @click="showSignHistory = false; fullSignRewards = false">收起签到历史</button>
        </template>
      </section>
      <button type="button" @click="navigate('/mine/prizes')">查看我的奖品申请与物流</button>
    </template>
    <button v-else type="button" class="primary" @click="openAuth('login')">登录后查询原记录</button>
  </section>
</template>

<style scoped>
.activity-history-recovery { margin-top: 24px; padding: 24px; border-radius: 12px; background: var(--kg-surface,#fff); color: var(--kg-text,#303746); font-size: 14px; line-height: 1.7; overflow-wrap: anywhere; }
h2 { margin: 0 0 8px; font-size: 20px; } h3 { margin: 0 0 12px; font-size: 16px; }
p { margin: 8px 0; } .activity-history-recovery > p { color: var(--kg-text-secondary,#687386); }
.history-receipt { padding: 16px; margin: 18px 0; border-radius: 10px; background: var(--kg-canvas,#f4f7fb); }
button { min-height: 44px; padding: 8px 16px; border: 1px solid var(--kg-line,#dce2e9); border-radius: 8px; background: var(--kg-surface,#fff); color: var(--kg-text,#303746); font: inherit; cursor: pointer; }
button:disabled { opacity: .55; cursor: default; } button:focus-visible { outline: 2px solid var(--kg-brand,#168ce3); outline-offset: 3px; }
[role='alert'] { color: var(--kg-danger,#b72f32); }
@media (max-width: 600px) { .activity-history-recovery { padding: 16px; } }
</style>
